// src/lib/intake/section1Facts.js
//
// Ticket 061 (Advisor Intake I4). Pure mapping between what the person answered
// on the Section 1 screens and the `domains` body of intake-start / intake-save,
// in both directions. No React, no network.
//
// ANSWERS SHAPE (what the reducer holds):
//   {
//     move:  { branch: null | 'before' | 'recent', month: '', year: '', day: '' },
//     facts: { <household key>: { status, value? } }   // answered keys only
//   }
// A key missing from `facts` means not reached yet: nothing is sent for it.
// No Section 1 question can be skipped, so a finished form has every applicable key.
// "Not sure" is { status: 'unknown' }, "None" is { status: 'known_zero' }.
// The browser never sends `source`; the server stamps it.
//
// MOVE DATE (plan D-M1, John's named exception to the facts-only rule; C is the
// oldest offered tax year, plan "Post-testing changes round 2"):
// the "before <C>" answer is sent as move_date = the shared placeholder date
// MOVE_DATE_BEFORE_CUTOFF. It is not a date the person gave. Any reader treats a
// move date older than the cutoff year as "before <C>" (isBeforeCutoff below).

import { MOVE_DATE_BEFORE_CUTOFF } from '../sharedOptions'
import { moveCutoffYear, moveMaxYear, SECTION1_SCREEN_ORDER, TAX_YEAR_SCREEN } from './intakeConfig'

export const SECTION1_HOUSEHOLD_KEYS = [
  'marital_status',
  'asset_split',
  'citizenship_status',
  'eu_citizen',
  'spouse_citizenship_status',
  'spouse_eu_citizen',
  'age_band',
  'spouse_age_band',
  'comunidad_autonoma',
  'move_date',
  'children_under_25',
  'children_under_3',
  'ascendants_over_65',
  'ascendants_over_75',
  'disability_taxpayer',
]

const ANSWERED = ['known_value', 'known_zero', 'unknown']

export const known = (value) => ({ status: 'known_value', value })
export const unknown = () => ({ status: 'unknown' })
export const none = () => ({ status: 'known_zero' })

export function emptyMove() {
  return { branch: null, month: '', year: '', day: '' }
}

// Filing history (screen 5.1, ticket 066): filed = null | true | false; gestor is
// asked only when filed is true. Neither can be skipped or "Not sure".
export function emptyFiling() {
  return { filed: null, gestor: null }
}

export function emptyAnswers() {
  return { move: emptyMove(), facts: {}, filing: emptyFiling() }
}

// A typed year: exactly four digits (so no sign, decimal point or e-notation),
// not earlier than the cutoff year C and not later than C + 10 (round 3).
export function isValidMoveYear(year, today = new Date()) {
  if (!/^\d{4}$/.test(String(year || ''))) return false
  const y = Number(year)
  return y >= moveCutoffYear(today) && y <= moveMaxYear(today)
}

// Typed input for the year and day boxes keeps digits only (the state never
// holds a sign, decimal point or "e"), capped at maxLen characters.
export function digitsOnly(value, maxLen) {
  return String(value == null ? '' : value).replace(/\D/g, '').slice(0, maxLen)
}

function pad(n) {
  return String(n).padStart(2, '0')
}

// A real calendar day: an integer written with 1-2 digits, 1 to 31 AND inside
// the month (no 30 February, leap years respected). Month is '1'..'12', year is
// four digits.
export function isValidMoveDay(year, month, day) {
  if (!/^\d{1,2}$/.test(String(day || ''))) return false
  const m = Number(month)
  const y = Number(year)
  if (!Number.isInteger(m) || m < 1 || m > 12 || !/^\d{4}$/.test(String(year))) return false
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return Number(day) >= 1 && Number(day) <= daysInMonth
}

// Same rule as the Snapshot (buildMoveDateISO): the day matters only for July
// (183-day residency rule), so it is collected and required only then. Every
// other month stores the first of the month as a dummy day (round 3, John).
// Returns null until the date is valid.
export const MOVE_DAY_MONTH = 7

export function moveNeedsDay(month) {
  return Number(month) === MOVE_DAY_MONTH
}

function realMoveDate(move, today = new Date()) {
  if (!isValidMoveYear(move.year, today)) return null
  const m = Number(move.month)
  if (!Number.isInteger(m) || m < 1 || m > 12) return null
  if (moveNeedsDay(move.month)) {
    if (!isValidMoveDay(move.year, move.month, move.day)) return null
    return `${move.year}-${pad(m)}-${pad(Number(move.day))}`
  }
  return `${move.year}-${pad(m)}-01`
}

// The move date that goes to the server, or null while the choice is incomplete.
export function moveDateFromMove(move, today = new Date()) {
  if (move.branch === 'before') return MOVE_DATE_BEFORE_CUTOFF
  if (move.branch === 'recent') return realMoveDate(move, today)
  return null
}

// True when a stored move date is older than the cutoff year. This covers the
// placeholder and any older real date. It is the ONE rule for "show this as
// 'before <C>', never as a date".
export function isBeforeCutoff(moveDate, today = new Date()) {
  if (typeof moveDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(moveDate)) return false
  return Number(moveDate.slice(0, 4)) < moveCutoffYear(today)
}

// Which follow-up keys apply, from the answers themselves (mirrors the server's
// normalizeApplicability so what is sent is what will be kept).
export function applicableKeys(facts) {
  const marital = facts.marital_status && facts.marital_status.status === 'known_value' ? facts.marital_status.value : null
  const citizenship = facts.citizenship_status && facts.citizenship_status.status === 'known_value' ? facts.citizenship_status.value : null
  const spouseCit = facts.spouse_citizenship_status && facts.spouse_citizenship_status.status === 'known_value' ? facts.spouse_citizenship_status.value : null
  const married = marital === 'married'
  return {
    asset_split: marital === 'married' || marital === 'civil_partnership',
    eu_citizen: citizenship === 'eu_citizen',
    spouse_citizenship_status: married,
    spouse_eu_citizen: married && spouseCit === 'eu_citizen',
    spouse_age_band: married,
  }
}

// Drops answers whose question no longer applies (for example the spouse answers
// after the person changes marital status away from married).
export function pruneFacts(facts) {
  const applies = applicableKeys(facts)
  const out = { ...facts }
  for (const key of Object.keys(applies)) {
    if (!applies[key]) delete out[key]
  }
  return out
}

function isZero(fact) {
  return !!fact && fact.status === 'known_zero'
}

// answers -> the `domains` body. Only household keys, only answered ones.
export function buildSection1Domains(answers, today = new Date()) {
  const facts = pruneFacts(answers.facts || {})
  const household = {}
  for (const key of SECTION1_HOUSEHOLD_KEYS) {
    if (key === 'move_date') continue
    if (facts[key]) household[key] = { ...facts[key] }
  }
  // "How many under 3 / over 75" is implied when the person has none at all.
  if (isZero(facts.children_under_25) && !facts.children_under_3) household.children_under_3 = none()
  if (isZero(facts.ascendants_over_65) && !facts.ascendants_over_75) household.ascendants_over_75 = none()

  const move = answers.move || emptyMove()
  const date = moveDateFromMove(move, today)
  if (date) {
    household.move_date = known(date)
    // Residency is derived from the move date, never asked. Always clear the
    // self-report so an answer stored by an earlier build cannot block the estimate.
    household.resident_self_report = { status: 'unanswered' }
  }
  const domains = { household }
  // Filing history is part of Section 1 (saved with it). gestor goes only with a
  // "Yes"; the server turns it into not_applicable when filed is false.
  const filing = answers.filing || emptyFiling()
  if (filing.filed !== null) {
    const filingHistory = { filed_spain_before: known(filing.filed) }
    if (filing.filed === true && filing.gestor !== null) filingHistory.has_gestor = known(filing.gestor)
    domains.filing_history = filingHistory
  }
  return domains
}

// The reverse: stored facts (the `facts` object from intake-load / intake-start,
// all domains) -> answers. Unanswered and not-applicable facts are left out.
// A stored move date older than the cutoff year loads as the "before C" branch
// WITHOUT rewriting the stored value.
export function answersFromFacts(allFacts, today = new Date()) {
  const household = (allFacts && allFacts.household) || {}
  const answers = emptyAnswers()
  for (const key of SECTION1_HOUSEHOLD_KEYS) {
    if (key === 'move_date') continue
    const fact = household[key]
    if (!fact || !ANSWERED.includes(fact.status)) continue
    answers.facts[key] = fact.status === 'known_value' ? known(fact.value) : { status: fact.status }
  }
  // Counts that are only implied by a zero total are not separate answers.
  if (isZero(answers.facts.children_under_25) && isZero(answers.facts.children_under_3)) delete answers.facts.children_under_3
  if (isZero(answers.facts.ascendants_over_65) && isZero(answers.facts.ascendants_over_75)) delete answers.facts.ascendants_over_75

  const move = household.move_date
  if (move && move.status === 'known_value' && typeof move.value === 'string') {
    if (isBeforeCutoff(move.value, today)) {
      answers.move = { branch: 'before', month: '', year: '', day: '' }
    } else {
      const [y, m, d] = move.value.split('-')
      answers.move = {
        branch: 'recent',
        month: String(Number(m)),
        year: y,
        // Only a July date has a real day; for other months the stored day is the dummy 1st.
        day: moveNeedsDay(m) ? String(Number(d)) : '',
      }
    }
  }
  answers.facts = pruneFacts(answers.facts)

  const filingHistory = (allFacts && allFacts.filing_history) || {}
  const filed = filingHistory.filed_spain_before
  if (filed && filed.status === 'known_value' && typeof filed.value === 'boolean') {
    answers.filing.filed = filed.value
    const gestor = filingHistory.has_gestor
    if (filed.value === true && gestor && gestor.status === 'known_value' && typeof gestor.value === 'boolean') {
      answers.filing.gestor = gestor.value
    }
  }
  return answers
}

// Which screen to resume on. Computed from the stored facts, never stored.
// Nothing can be skipped, so a screen is open until the same completeness check
// the Next button uses passes (this also reopens a screen whose only answer is a
// "Not sure" that the screen no longer allows, such as region). The tax-year
// screen is never reopened (the year was needed to load). Returns null when every
// screen is done.
export function firstOpenScreen(allFacts, today = new Date()) {
  const answers = answersFromFacts(allFacts, today)
  for (const id of SECTION1_SCREEN_ORDER) {
    if (id === TAX_YEAR_SCREEN) continue
    if (!isScreenComplete(id, { answers, taxYear: null, yearOptions: [], today })) return id
  }
  return null
}

// Can the person press Next on this screen? `ctx` = { answers, taxYear, yearOptions }.
export function isScreenComplete(id, ctx) {
  const { answers, taxYear, yearOptions = [], today = new Date() } = ctx
  const facts = answers.facts
  const has = (key) => !!facts[key]
  const knownIs = (key) => !!facts[key] && facts[key].status === 'known_value'
  const applies = applicableKeys(facts)
  switch (id) {
    case '1.2': {
      const m = answers.move
      if (m.branch === 'before') return true
      if (m.branch === 'recent') return moveDateFromMove(m, today) !== null
      return false
    }
    case '1.1':
      return taxYear != null && yearOptions.includes(taxYear)
    case '1.3':
      return knownIs('comunidad_autonoma')
    case '1.4':
      return knownIs('citizenship_status') && (!applies.eu_citizen || has('eu_citizen'))
    case '1.5':
      return knownIs('marital_status')
        && (!applies.asset_split || has('asset_split'))
        && (!applies.spouse_citizenship_status || has('spouse_citizenship_status'))
        && (!applies.spouse_eu_citizen || has('spouse_eu_citizen'))
    case '1.6':
      return knownIs('age_band') && (!applies.spouse_age_band || knownIs('spouse_age_band'))
    case '1.7':
      return countsComplete(facts)
    case '5.1': {
      const filing = answers.filing || emptyFiling()
      return filing.filed === false || (filing.filed === true && filing.gestor !== null)
    }
    default:
      return false
  }
}

function countOf(fact) {
  if (!fact) return null
  if (fact.status === 'known_zero') return 0
  if (fact.status === 'known_value') return fact.value
  return null
}

// A subset count (under 3, over 75) may not exceed its total.
export function countsProblem(facts) {
  const pairs = [['children_under_3', 'children_under_25'], ['ascendants_over_75', 'ascendants_over_65']]
  for (const [part, whole] of pairs) {
    const p = countOf(facts[part])
    const w = countOf(facts[whole])
    if (p !== null && w !== null && p > w) return `${part} cannot be more than ${whole}`
  }
  return null
}

function countsComplete(facts) {
  const groups = [['children_under_25', 'children_under_3'], ['ascendants_over_65', 'ascendants_over_75']]
  for (const [whole, part] of groups) {
    if (!facts[whole]) return false
    if (facts[whole].status === 'known_value' && !facts[part]) return false
  }
  if (!facts.disability_taxpayer) return false
  return countsProblem(facts) === null
}
